/**
 * World-space text labels (+field): portal names, town NPC names + titles, quest markers. Canvas
 * textures on screen-sized sprites (sizeAttenuation off: the same pixel size at any distance),
 * drawn on top of the scene (no depth test) so they read across a field, like WoW nameplates.
 */
import * as THREE from 'three';

export interface LabelOpts {
  title?: string;
  /** Name colour (CSS). */
  color?: string;
  titleColor?: string;
  /** Name font size in canvas px (the sprite keeps that pixel size on a 1000 px tall view). */
  size?: number;
}

/** Fraction of the view height per canvas pixel (sizeAttenuation off: scale is in view units at distance 1). */
const PX = 0.00062;

export function makeLabel(name: string, o: LabelOpts = {}): THREE.Sprite {
  const size = o.size ?? 30;
  const tsize = Math.round(size * 0.72);
  const pad = 10;
  const cv = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  const font = `600 ${size}px "Trebuchet MS", "Segoe UI", sans-serif`;
  const tfont = `italic 500 ${tsize}px "Trebuchet MS", "Segoe UI", sans-serif`;
  let w = 256;
  let h = 64;
  if (cv) {
    const g = cv.getContext('2d')!;
    g.font = font;
    const nw = g.measureText(name).width;
    g.font = tfont;
    const tw = o.title ? g.measureText(`<${o.title}>`).width : 0;
    w = Math.ceil(Math.max(nw, tw) + pad * 2 + 8);
    h = Math.ceil(size + (o.title ? tsize + 6 : 0) + pad * 2);
    cv.width = w;
    cv.height = h;
    g.textAlign = 'center';
    g.textBaseline = 'top';
    g.lineJoin = 'round';
    const draw = (text: string, f: string, y: number, fill: string, stroke: number): void => {
      g.font = f;
      g.lineWidth = stroke;
      g.strokeStyle = 'rgba(10,12,20,0.92)';
      g.strokeText(text, w / 2, y);
      g.fillStyle = fill;
      g.fillText(text, w / 2, y);
    };
    draw(name, font, pad, o.color ?? '#ffe9a8', 6);
    if (o.title) draw(`<${o.title}>`, tfont, pad + size + 6, o.titleColor ?? '#d8e2ee', 5);
  }
  const tex = cv ? new THREE.CanvasTexture(cv) : new THREE.Texture();
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.minFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false, depthWrite: false, sizeAttenuation: false });
  const s = new THREE.Sprite(mat);
  s.center.set(0.5, 0);
  s.scale.set(w * PX, h * PX, 1);
  s.renderOrder = 20;
  s.userData['labelPx'] = [w, h];
  return s;
}

/** Quest marker glyph ('!' / '?'), gold or grey, with a dark outline and a soft halo. */
export function makeMarkerTexture(glyph: '!' | '?', grey: boolean): THREE.Texture {
  const cv = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  if (!cv) return new THREE.Texture();
  cv.width = 96;
  cv.height = 128;
  const g = cv.getContext('2d')!;
  const halo = g.createRadialGradient(48, 64, 4, 48, 64, 48);
  halo.addColorStop(0, grey ? 'rgba(200,200,200,0.25)' : 'rgba(255,214,90,0.45)');
  halo.addColorStop(1, 'rgba(255,214,90,0)');
  g.fillStyle = halo;
  g.fillRect(0, 0, 96, 128);
  g.font = '900 104px "Trebuchet MS", "Segoe UI", sans-serif';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.lineJoin = 'round';
  g.lineWidth = 12;
  g.strokeStyle = 'rgba(40,24,6,0.95)';
  g.strokeText(glyph, 48, 68);
  const fill = g.createLinearGradient(0, 20, 0, 116);
  if (grey) {
    fill.addColorStop(0, '#e2e2e2');
    fill.addColorStop(1, '#8a8a8a');
  } else {
    fill.addColorStop(0, '#fff6b0');
    fill.addColorStop(0.5, '#ffd23c');
    fill.addColorStop(1, '#e08a10');
  }
  g.fillStyle = fill;
  g.fillText(glyph, 48, 68);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export function disposeLabel(s: THREE.Sprite): void {
  s.material.map?.dispose();
  s.material.dispose();
  s.removeFromParent();
}

/** Scale a label for the current view height (keeps its canvas pixel size). */
export function labelPixelScale(s: THREE.Sprite, viewH: number, k = 1): void {
  const px = s.userData['labelPx'] as [number, number] | undefined;
  if (!px) return;
  const f = (1000 / Math.max(300, viewH)) * PX * k;
  s.scale.set(px[0] * f, px[1] * f, 1);
}
